package diana.soleil.design.iraniancanadienbazzar.feature.events

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EventCard as DsEventCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EventRailCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MediaCardSkeleton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SectionHeader
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.WindowWidthClass
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.windowWidthClassFor
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.EventCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.EventVenue
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.cityLabel
import diana.soleil.design.iraniancanadienbazzar.core.model.localizeDigits
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.*
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import androidx.compose.runtime.LaunchedEffect
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_going
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_im_going
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_featured_section
import diana.soleil.design.iraniancanadienbazzar.feature.events.resources.events_going_count
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun EventsRoute(
    modifier: Modifier = Modifier,
    onGetFreeTickets: (eventId: String) -> Unit = {},
    onOpenEventDetail: (eventId: String) -> Unit = {},
    onRequireSignIn: () -> Unit = {},
    /** Null when Events is hosted as a root surface; non-null renders a back arrow. */
    onBack: (() -> Unit)? = null,
    viewModel: EventsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val rsvpGate by viewModel.rsvpSignInRequired.collectAsStateWithLifecycle()
    LaunchedEffect(rsvpGate) {
        if (rsvpGate) {
            viewModel.rsvpSignInRequired.value = false
            onRequireSignIn()
        }
    }
    // Same share plumbing as business/marketplace/feed: platform ShareService + RC-driven base URL.
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    EventsScreen(
        state = state,
        onFilter = viewModel::setFilter,
        onSort = viewModel::setSort,
        onRetry = viewModel::retry,
        // A tapped card opens the full event page (which then offers tickets/RSVP/share), instead of
        // jumping straight to the ticket claim / external link.
        onOpenEvent = { event -> onOpenEventDetail(event.id) },
        onShareEvent = { event, title ->
            shareService.share(listOf(title, shareConfig.eventLink(event.id)).joinToString("\n"))
        },
        onToggleGoing = viewModel::toggleGoing,
        onBack = onBack,
        modifier = modifier,
    )
}

/** Stitch `3909346f` (mobile) + `60f84dbe` (desktop), organizer outlinks only. */
@Composable
fun EventsScreen(
    state: EventsUiState,
    onFilter: (EventFilter) -> Unit,
    onRetry: () -> Unit,
    onOpenEvent: (Event) -> Unit,
    onShareEvent: (Event, String) -> Unit,
    modifier: Modifier = Modifier,
    onSort: (EventSort) -> Unit = {},
    onToggleGoing: (eventId: String, going: Boolean) -> Unit = { _, _ -> },
    onBack: (() -> Unit)? = null,
) {
    if (state.error) {
        ErrorState(
            message = stringResource(Res.string.events_error),
            onRetry = onRetry,
            retryLabel = stringResource(Res.string.events_retry),
            modifier = modifier,
        )
        return
    }
    BoxWithConstraints(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        val widthClass = windowWidthClassFor(maxWidth)
        val columns = when (widthClass) {
            WindowWidthClass.Compact -> 1
            WindowWidthClass.Medium -> 2
            WindowWidthClass.Expanded -> 3
        }
        LazyColumn(
            modifier = Modifier
                .fillMaxHeight()
                .statusBarsPadding()
                .widthIn(max = AppTheme.sizes.contentMaxWidth)
                .align(Alignment.TopCenter),
            contentPadding = PaddingValues(
                start = if (widthClass == WindowWidthClass.Expanded) {
                    AppTheme.spacing.containerMarginDesktop
                } else {
                    AppTheme.spacing.containerMarginMobile
                },
                end = if (widthClass == WindowWidthClass.Expanded) {
                    AppTheme.spacing.containerMarginDesktop
                } else {
                    AppTheme.spacing.containerMarginMobile
                },
                top = AppTheme.spacing.lg,
                bottom = AppTheme.spacing.xl,
            ),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
        ) {
            item {
                Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                    // Events is a pushed route since it left the bottom nav (2026-08-02), so the
                    // title row carries a back affordance. `onBack == null` keeps it headerless for
                    // any host that still shows it as a root surface.
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    ) {
                        if (onBack != null) {
                            AppIconButton(
                                iconName = "arrow_back",
                                contentDescription = stringResource(Res.string.events_back_a11y),
                                onClick = onBack,
                                tint = AppTheme.colors.onSurface,
                            )
                        }
                        Text(
                            text = stringResource(Res.string.events_title),
                            style = AppTheme.typography.headlineLg,
                            color = AppTheme.colors.onSurface,
                        )
                    }
                    Text(
                        text = stringResource(Res.string.events_subtitle),
                        style = AppTheme.typography.bodyMd,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                }
            }
            item { EventFilters(state.filter, onFilter) }
            item { EventSortChips(state.sort, onSort) }
            if (state.loading) {
                // Shimmer skeletons shaped like event cards.
                items(2) { MediaCardSkeleton() }
            } else if (state.events.isEmpty()) {
                item {
                    EmptyState(
                        title = stringResource(Res.string.events_empty_title),
                        description = stringResource(Res.string.events_empty_description),
                    )
                }
            } else {
                // Paid placements — horizontal rail (multiple sponsors scroll side by side).
                if (state.featuredList.isNotEmpty()) {
                    item { SectionHeader(title = stringResource(Res.string.events_featured_section)) }
                    item {
                        LazyRow(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                            items(state.featuredList, key = { it.id }) { featured ->
                                EventRailItem(
                                    event = featured,
                                    featured = true,
                                    going = featured.id in state.going,
                                    count = state.goingCounts[featured.id] ?: 0,
                                    onOpenEvent = onOpenEvent,
                                    onToggle = { onToggleGoing(featured.id, featured.id !in state.going) },
                                )
                            }
                        }
                    }
                }
                // The caller's own RSVPs, one swipe away — their personal agenda.
                if (state.goingList.isNotEmpty()) {
                    item { SectionHeader(title = stringResource(Res.string.events_im_going)) }
                    item {
                        LazyRow(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                            items(state.goingList, key = { "going_" + it.id }) { event ->
                                EventRailItem(
                                    event = event,
                                    going = true,
                                    count = state.goingCounts[event.id] ?: 0,
                                    onOpenEvent = onOpenEvent,
                                    onToggle = { onToggleGoing(event.id, false) },
                                )
                            }
                        }
                    }
                }
                if (state.upcoming.isNotEmpty()) {
                    item { SectionHeader(title = stringResource(Res.string.events_upcoming)) }
                    items(state.upcoming.chunked(columns), key = { row -> row.first().id }) { row ->
                        Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg)) {
                            row.forEach { event ->
                                Box(modifier = Modifier.weight(1f)) {
                                    Column {
                                        EventCard(event = event, onOpenEvent = onOpenEvent, onShareEvent = onShareEvent)
                                        RsvpRow(
                                            going = event.id in state.going,
                                            count = state.goingCounts[event.id] ?: 0,
                                            onToggle = { onToggleGoing(event.id, event.id !in state.going) },
                                        )
                                    }
                                }
                            }
                            repeat(columns - row.size) { Spacer(modifier = Modifier.weight(1f)) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EventFilters(selected: EventFilter, onFilter: (EventFilter) -> Unit) {
    Row(
        modifier = Modifier.horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        EventFilter.entries.forEach { filter ->
            AppChip(
                label = filter.label(),
                selected = selected == filter,
                onClick = { onFilter(filter) },
            )
        }
    }
}

/** Sort row under the filters: shared appeal ranking (default) vs plain date order. */
@Composable
private fun EventSortChips(selected: EventSort, onSort: (EventSort) -> Unit) {
    Row(
        modifier = Modifier.horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = stringResource(Res.string.events_sort_label),
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        AppChip(
            label = stringResource(Res.string.events_sort_recommended),
            selected = selected == EventSort.Recommended,
            onClick = { onSort(EventSort.Recommended) },
        )
        AppChip(
            label = stringResource(Res.string.events_sort_soonest),
            selected = selected == EventSort.Soonest,
            leadingIcon = "schedule",
            onClick = { onSort(EventSort.Soonest) },
        )
    }
}

@Composable
private fun EventCard(
    event: Event,
    onOpenEvent: (Event) -> Unit,
    onShareEvent: (Event, String) -> Unit,
    featured: Boolean = false,
    imageAspectRatio: Float = 16f / 9f,
) {
    val title = event.title.resolve(AppTheme.language)
    // The button (and the whole card) now open the in-app EventDetail page, which itself carries the
    // ticket/RSVP CTAs — so it's always enabled and labelled generically, never a dead ticket button.
    DsEventCard(
        title = title,
        dateTime = eventDateTime(event.startsAtMillis),
        venue = stringResource(Res.string.events_venue_format, event.venue.name, AppTheme.language.cityLabel(event.venue.city)),
        actionLabel = stringResource(Res.string.events_view_details),
        imageContentDescription = stringResource(Res.string.events_image_a11y, title),
        onAction = { onOpenEvent(event) },
        onClick = { onOpenEvent(event) },
        actionTrailingIcon = "chevron_right",
        coverUrl = event.coverUrl,
        badgeLabel = when {
            event.isFree -> stringResource(Res.string.events_free)
            featured -> stringResource(Res.string.events_featured)
            else -> null
        },
        actionEnabled = true,
        featured = featured,
        imageAspectRatio = imageAspectRatio,
        shareContentDescription = stringResource(Res.string.events_share_a11y),
        onShare = { onShareEvent(event, title) },
    )
}

/** Compact rail entry — resolves copy for [EventRailCard]; whole card opens the event. */
@Composable
private fun EventRailItem(
    event: Event,
    going: Boolean,
    count: Int,
    onOpenEvent: (Event) -> Unit,
    onToggle: () -> Unit,
    featured: Boolean = false,
) {
    val title = event.title.resolve(AppTheme.language)
    EventRailCard(
        title = title,
        dateTime = eventDateTime(event.startsAtMillis),
        venue = stringResource(Res.string.events_venue_format, event.venue.name, AppTheme.language.cityLabel(event.venue.city)),
        imageContentDescription = stringResource(Res.string.events_image_a11y, title),
        onClick = { onOpenEvent(event) },
        coverUrl = event.coverUrl,
        badgeLabel = when {
            event.isFree -> stringResource(Res.string.events_free)
            featured -> stringResource(Res.string.events_featured)
            else -> null
        },
        goingCountLabel = if (count > 0) stringResource(Res.string.events_going_count, count) else null,
        rsvpGoing = going,
        rsvpContentDescription = stringResource(if (going) Res.string.events_going else Res.string.events_im_going),
        onToggleRsvp = onToggle,
        modifier = Modifier.width(AppTheme.sizes.eventRailCardWidth),
    )
}

/** "I'm going" toggle + attendee count under an event card (Iroonia-parity social proof). */
@Composable
private fun RsvpRow(going: Boolean, count: Int, onToggle: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        AppButton(
            text = stringResource(if (going) Res.string.events_going else Res.string.events_im_going),
            onClick = onToggle,
            variant = if (going) AppButtonVariant.Secondary else AppButtonVariant.Text,
            leadingIcon = if (going) "check" else "event_available",
        )
        if (count > 0) {
            Text(
                text = stringResource(Res.string.events_going_count, count),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun EventFilter.label(): String = stringResource(
    when (this) {
        EventFilter.All -> Res.string.events_filter_all
        EventFilter.ThisWeek -> Res.string.events_filter_week
        EventFilter.Free -> Res.string.events_filter_free
        EventFilter.Music -> Res.string.events_filter_music
        EventFilter.Culture -> Res.string.events_filter_culture
        EventFilter.Family -> Res.string.events_filter_family
        EventFilter.Business -> Res.string.events_filter_business
    },
)

@Composable
internal fun eventDateTime(epochMillis: Long): String {
    val value = Instant.fromEpochMilliseconds(epochMillis)
        .toLocalDateTime(TimeZone.currentSystemDefault())
    val hour12 = when {
        value.hour == 0 -> 12
        value.hour > 12 -> value.hour - 12
        else -> value.hour
    }
    // Persian digits in Farsi: resource args always render Western digits (see NumberFormatting).
    return AppTheme.language.localizeDigits(
        stringResource(
            Res.string.events_date_time_format,
            value.month.monthLabel(),
            value.day,
            hour12,
            value.minute.toString().padStart(2, '0'),
            if (value.hour < 12) {
                stringResource(Res.string.events_am)
            } else {
                stringResource(Res.string.events_pm)
            },
        ),
    )
}

@Composable
internal fun Month.monthLabel(): String = stringResource(
    when (this) {
        Month.JANUARY -> Res.string.events_month_jan
        Month.FEBRUARY -> Res.string.events_month_feb
        Month.MARCH -> Res.string.events_month_mar
        Month.APRIL -> Res.string.events_month_apr
        Month.MAY -> Res.string.events_month_may
        Month.JUNE -> Res.string.events_month_jun
        Month.JULY -> Res.string.events_month_jul
        Month.AUGUST -> Res.string.events_month_aug
        Month.SEPTEMBER -> Res.string.events_month_sep
        Month.OCTOBER -> Res.string.events_month_oct
        Month.NOVEMBER -> Res.string.events_month_nov
        Month.DECEMBER -> Res.string.events_month_dec
    },
)

internal fun String.normalizedWebUrl(): String =
    if (startsWith("http://") || startsWith("https://")) this else "https://$this"

private val previewEvents = listOf(
    Event(
        id = "nowruz",
        organizerUid = "organizer",
        title = LocalizedText(en = "Nowruz Gala 2030"),
        startsAtMillis = 1_900_270_800_000,
        venue = EventVenue("Liberty Grand", city = "Toronto"),
        category = EventCategory.Culture,
        ticketUrl = "https://example.org",
        featured = true,
    ),
    Event(
        id = "poetry",
        organizerUid = "organizer",
        title = LocalizedText(en = "Persian Poetry Night"),
        startsAtMillis = 1_900_700_400_000,
        venue = EventVenue("Vancouver Public Library", city = "Vancouver"),
        category = EventCategory.Culture,
        ticketUrl = "https://example.org",
        isFree = true,
    ),
)

@Preview(name = "Events compact", widthDp = 390, heightDp = 844)
@Composable
private fun EventsCompactPreview() {
    AppTheme {
        EventsScreen(
            state = EventsUiState(loading = false, events = previewEvents),
            onFilter = {},
            onRetry = {},
            onOpenEvent = {},
            onShareEvent = { _, _ -> },
        )
    }
}

@Preview(name = "Events expanded", widthDp = 1200, heightDp = 900)
@Composable
private fun EventsExpandedPreview() {
    AppTheme {
        EventsScreen(
            state = EventsUiState(loading = false, events = previewEvents),
            onFilter = {},
            onRetry = {},
            onOpenEvent = {},
            onShareEvent = { _, _ -> },
        )
    }
}
